/**
 * Centralized query keys + fetcher functions for TanStack Query.
 * Each `keys.x()` returns a stable tuple that callers pass to useQuery.
 */
import { api } from './api';
import type {
  Agent,
  ApiKey,
  AppNotification,
  Container,
  ContainerMetadata,
  ContainerNewBootstrap,
  EffectiveResources,
  ExternalDomain,
  Group,
  Job,
  JobStatusRow,
  MailAccount,
  MailDomain,
  MailDnsInfo,
  MailHostInfo,
  MailPtrInfo,
  MailSuppression,
  Node,
  NodeStats,
  AppSettings,
  ResourceRequest,
  Site,
  UsageReport,
  User,
} from './types';

export const keys = {
  sites: () => ['sites'] as const,
  site: (id: number | string) => ['sites', String(id)] as const,
  nodes: (siteId: number | string) => ['sites', String(siteId), 'nodes'] as const,
  node: (siteId: number | string, id: number | string) =>
    ['sites', String(siteId), 'nodes', String(id)] as const,
  nodeStats: (siteId: number | string, id: number | string) =>
    ['sites', String(siteId), 'nodes', String(id), 'stats'] as const,
  usage: (siteId: number | string) => ['sites', String(siteId), 'usage'] as const,
  containers: (siteId: number | string, params?: Record<string, string | string[] | undefined>) =>
    ['sites', String(siteId), 'containers', params ?? {}] as const,
  container: (siteId: number | string, id: number | string) =>
    ['sites', String(siteId), 'containers', String(id)] as const,
  containerBootstrap: (siteId: number | string) =>
    ['sites', String(siteId), 'containers', 'new'] as const,
  containerMetadata: (image: string) => ['container-metadata', image] as const,
  externalDomains: () => ['external-domains'] as const,
  externalDomain: (id: number | string) => ['external-domains', String(id)] as const,
  agents: () => ['agents'] as const,
  notifications: () => ['notifications'] as const,
  users: () => ['users'] as const,
  user: (uid: number | string) => ['users', String(uid)] as const,
  groups: () => ['groups'] as const,
  group: (id: number | string) => ['groups', String(id)] as const,
  apikeys: () => ['apikeys'] as const,
  mailAccounts: () => ['mail-accounts'] as const,
  mailDomains: () => ['mail-accounts', 'domains'] as const,
  mailSuppressions: (id: string) => ['mail-accounts', id, 'suppressions'] as const,
  mailDns: (domainId: number | string) => ['external-domains', String(domainId), 'mail-dns'] as const,
  mailHost: () => ['mail', 'host'] as const,
  settings: () => ['settings'] as const,
  job: (id: number | string) => ['jobs', String(id)] as const,
  jobStatuses: (id: number | string) => ['jobs', String(id), 'statuses'] as const,
  resourceRequests: (status?: string) => ['resource-requests', status || 'all'] as const,
  resourceRequestCount: () => ['resource-requests', 'count'] as const,
  effectiveResources: (siteId: number | string, hostname: string, username: string) =>
    ['resource-requests', 'effective', String(siteId), hostname, username] as const,
};

export const queries = {
  // Sites
  listSites: () => api.get<Site[]>('/api/v1/sites'),
  getSite: (id: number | string) => api.get<Site>(`/api/v1/sites/${id}`),

  // Nodes
  listNodes: (siteId: number | string) =>
    api.get<Node[]>(`/api/v1/sites/${siteId}/nodes`),
  getNode: (siteId: number | string, id: number | string) =>
    api.get<Node>(`/api/v1/sites/${siteId}/nodes/${id}`),
  getNodeStats: (siteId: number | string, id: number | string) =>
    api.get<NodeStats>(`/api/v1/sites/${siteId}/nodes/${id}/stats`),

  // Usage
  getUsage: (siteId: number | string) =>
    api.get<UsageReport>(`/api/v1/sites/${siteId}/usage`),

  // Agents
  listAgents: () => api.get<Agent[]>('/api/v1/agents'),

  // Notifications (owner-scoped). The list is unacked-first + newest-first, so
  // the bell derives its unread badge from the returned rows.
  listNotifications: () => api.get<AppNotification[]>('/api/v1/notifications'),
  ackNotification: (id: number) =>
    api.post<AppNotification>(`/api/v1/notifications/${id}/ack`),
  ackAllNotifications: () =>
    api.post<{ acknowledged: number }>('/api/v1/notifications/all/ack'),

  // Containers
  listContainers: (
    siteId: number | string,
    params?: { user?: string[]; nodeId?: string; hostname?: string },
  ) => {
    const qs = new URLSearchParams();
    // `user` is sent in bracket notation (user[0]=..., user[1]=...) so the
    // server's 'extended' query parser always receives an array. Omitted/empty
    // returns everything the caller may see server-side.
    params?.user?.forEach((u, i) => qs.set(`user[${i}]`, u));
    if (params?.nodeId) qs.set('nodeId', params.nodeId);
    if (params?.hostname) qs.set('hostname', params.hostname);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return api.get<Container[]>(`/api/v1/sites/${siteId}/containers${suffix}`);
  },
  getContainer: (siteId: number | string, id: number | string) =>
    api.get<Container>(`/api/v1/sites/${siteId}/containers/${id}`),
  // Container sharing (collaborators). Both return the updated collaborator
  // username list.
  shareContainer: (siteId: number | string, id: number | string, username: string) =>
    api.post<{ collaborators: string[] }>(
      `/api/v1/sites/${siteId}/containers/${id}/collaborators`,
      { username },
    ),
  unshareContainer: (siteId: number | string, id: number | string, username: string) =>
    api.delete<{ collaborators: string[] }>(
      `/api/v1/sites/${siteId}/containers/${id}/collaborators/${encodeURIComponent(username)}`,
    ),
  transferContainerOwnership: (siteId: number | string, id: number | string, username: string) =>
    api.put<{ containerId: number; message: string }>(
      `/api/v1/sites/${siteId}/containers/${id}`,
      { username },
    ),
  containerBootstrap: (siteId: number | string) =>
    api.get<ContainerNewBootstrap>(`/api/v1/sites/${siteId}/containers/new`),
  containerMetadata: (siteId: number | string, image: string) =>
    api.get<ContainerMetadata>(
      `/api/v1/sites/${siteId}/containers/metadata?image=${encodeURIComponent(image)}`,
    ),

  // External domains
  listExternalDomains: () => api.get<ExternalDomain[]>('/api/v1/external-domains'),
  getExternalDomain: (id: number | string) =>
    api.get<ExternalDomain>(`/api/v1/external-domains/${id}`),

  // Users
  listUsers: () => api.get<User[]>('/api/v1/users'),
  getUser: (uid: number | string) => api.get<User>(`/api/v1/users/${uid}`),

  // Groups
  listGroups: () => api.get<Group[]>('/api/v1/groups'),
  getGroup: (id: number | string) => api.get<Group>(`/api/v1/groups/${id}`),

  // API keys
  listApiKeys: () => api.get<ApiKey[]>('/api/v1/apikeys'),

  // Mail accounts (issue #67)
  listMailAccounts: () => api.get<MailAccount[]>('/api/v1/mail-accounts'),
  listMailDomains: () => api.get<MailDomain[]>('/api/v1/mail-accounts/domains'),
  listMailSuppressions: (id: string) =>
    api.get<MailSuppression[]>(`/api/v1/mail-accounts/${id}/suppressions`),
  getDomainMailDns: (domainId: number | string) =>
    api.get<MailDnsInfo>(`/api/v1/external-domains/${domainId}/mail/dns`),
  getMailHost: () => api.get<MailHostInfo>('/api/v1/agents/mail-host'),
  getMailPtr: () => api.get<MailPtrInfo>('/api/v1/agents/mail-host/ptr'),

  // Settings
  getSettings: () => api.get<AppSettings>('/api/v1/settings'),

  // Jobs
  getJob: (id: number | string) => api.get<Job>(`/api/v1/jobs/${id}`),
  getJobStatuses: (id: number | string, offset = 0, limit = 1000) =>
    api.get<JobStatusRow[]>(`/api/v1/jobs/${id}/status?offset=${offset}&limit=${limit}`),

  // Resource Requests
  listResourceRequests: (status?: string) =>
    api.get<ResourceRequest[]>(
      `/api/v1/resource-requests${status ? `?status=${status}` : ''}`,
    ),
  getResourceRequestCount: () =>
    api.get<{ count: number }>('/api/v1/resource-requests/count'),
  getEffectiveResources: (siteId: number | string, hostname: string, username: string) =>
    api.get<EffectiveResources>(
      `/api/v1/resource-requests/effective/${siteId}/${encodeURIComponent(hostname)}/${encodeURIComponent(username)}`,
    ),
};
